import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { Fragment, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Settings, ArrowLeftRight, LogOut } from "lucide-react";
import { UserContext } from "../../../context/UserContext";
import PurchaserClient from "../../../clients/PurchaserClient.ts";
import UserIcon from "../../../components/Common/UserIcon.tsx";
import Portal from "../../DesignSystem/Portal.tsx";
import { userDisplayName } from "../../../util/formatting.tsx";
import Tooltip from "../../DesignSystem/Tooltip.tsx";
import Button from "../../DesignSystem/Button.tsx";

interface UserDropdownProps {
    sidebarShown?: boolean;
}

export default function UserDropdown({ sidebarShown }: UserDropdownProps) {
    const { user, logout } = useContext(UserContext);
    const navigate = useNavigate();

    // gets a new JWT from the API and then reloads the page for the team switch to take effect
    const switchTeam = (teamId: number) => PurchaserClient.switchTeam(teamId).then(() => window.location.reload());

    // While /me is loading, show a gray square placeholder where the avatar will be (matches
    // UserIcon's h-5 footprint) instead of leaving the top-right empty.
    if (!user) {
        return (
            <div className="hidden p-1.5 md:block">
                <div className="h-5 w-5 animate-pulse rounded-sm bg-stone-200" />
            </div>
        );
    }

    return (
        <Menu as="div" className="hidden md:block">
            <MenuButton>
                {({ open }: { open: boolean }) => (
                    <Tooltip active={!open} text="User Menu" align="bottom" className="focus:outline-none">
                        <div className={`cursor-pointer rounded-sm p-1.5 ${open ? "bg-stone-300" : ""} hover:bg-stone-200 active:bg-stone-300`}>
                            <UserIcon iconBackgroundColorClass={user.iconBackgroundColorClass} firstName={user.first_name} />
                        </div>
                    </Tooltip>
                )}
            </MenuButton>
            <Portal>
                <div className="absolute z-500">
                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                    >
                        <MenuItems className="fixed top-12 right-3 z-20 -mt-1 w-[280px] origin-top-right rounded-2xl border border-stone-200 bg-white shadow focus:outline-none">
                            <div className="p-3">
                                <div className="mb-3 flex border-b border-stone-200 pb-2.5">
                                    <div className="mx-1.5 mt-0.5">
                                        <UserIcon iconBackgroundColorClass={user.iconBackgroundColorClass} firstName={user.first_name} />
                                    </div>
                                    <div>
                                        <div>{userDisplayName(user)}</div>
                                        <div className="text-xs text-stone-600">{user.email}</div>
                                    </div>
                                </div>
                                <MenuItem as="div">
                                    <Button
                                        color="clear"
                                        text="User Settings"
                                        icon={<Settings className="h-4 w-4 stroke-stone-400" />}
                                        className="mb-2 w-full"
                                        linkClassName="justify-start"
                                        href="/settings/user/profile"
                                    />
                                </MenuItem>

                                <MenuItem as="div">
                                    <Button
                                        color="clear"
                                        text="Sign Out"
                                        icon={<LogOut className="h-4 w-4 stroke-stone-400" />}
                                        className="w-full"
                                        onClick={async () => {
                                            await logout();
                                            navigate("/login");
                                        }}
                                    />
                                </MenuItem>
                            </div>
                        </MenuItems>
                    </Transition>
                </div>
            </Portal>
        </Menu>
    );
}
